import AppButton from '@/components/ui/AppButton';
import { AlertTriangle, X } from 'lucide-react';

interface AppRemoveModalProps {
  // New API (preferred)
  onClose?: () => void;
  onSubmit?: () => void;
  title?: string;
  text?: string;
  // Legacy API
  removeHandler?: () => void;
  removeText?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

export default function AppRemoveModal({
  onClose,
  onSubmit,
  title,
  text,
  removeHandler,
  removeText,
  open,
  setOpen,
}: AppRemoveModalProps) {
  // Support both old API (open/setOpen) and new API (always visible when rendered)
  const isVisible = open !== undefined ? open : true;
  const handleClose = onClose || (() => setOpen?.(false));
  const handleConfirm = onSubmit || removeHandler || (() => {});
  const displayTitle = title || 'Remove Item';
  const displayText = text || removeText || 'Are you sure you want to remove this item? This action cannot be undone.';

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-sm rounded-xl border border-coal-200 bg-white shadow-dropdown animate-in fade-in-0 zoom-in-95 duration-200">
          <button
            className="absolute right-3 top-3 rounded-md p-1.5 text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-600"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </button>
          
          <div className="flex flex-col items-center px-6 pt-8 pb-6">
            {/* Icon */}
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
              <AlertTriangle className="h-6 w-6 text-danger" />
            </div>
            
            <h3 className="text-lg font-semibold text-coal-900">{displayTitle}</h3>
            <p className="mt-2 text-center text-sm leading-relaxed text-coal-500">
              {displayText}
            </p>
          </div>
          
          <div className="flex gap-3 border-t border-coal-100 px-6 py-4">
            <AppButton onClick={handleClose} theme="default" className="flex-1">
              Cancel
            </AppButton>
            <AppButton onClick={handleConfirm} theme="error" className="flex-1">
              Remove
            </AppButton>
          </div>
        </div>
      </div>
    </>
  );
}
